import {
	mergeMap as _observableMergeMap,
	catchError as _observableCatch,
	map,
	tap,
} from 'rxjs/operators';
import {
	Observable,
	throwError as _observableThrow,
	of as _observableOf,
} from 'rxjs';
import { Injectable } from '@angular/core';
import {
	HttpClient,
	HttpHeaders,
	HttpResponse,
	HttpResponseBase,
} from '@angular/common/http';
import { environment } from 'src/environments/environment';
import * as moment from 'moment';
import { getFilenameFromHttpResponse } from 'src/shared/utilities/utility-functions';
import {
	InvoiceBundleReportRequest,
	DwrListArgs,
	BillableJobListArgs,
} from 'src/shared/api';
import { PayrollMonthlyReportArgs } from 'src/admin-app/jobs/shared/payroll-monthly-report-dialog/payroll-monthly-report-dialog.component';
import { InvoiceSummaryArgs } from 'src/admin-app/invoicing/historical-invoice-list/historical-invoice-list.utility';

@Injectable({
	providedIn: 'root',
})
export class ReportsService {
	protected jsonParseReviver: ((key: string, value: any) => any) | undefined =
		undefined;
	constructor(private http: HttpClient) {
		this.http = http;
	}

	invoiceBundleReport(request: InvoiceBundleReportRequest): Observable<any> {
		const url_ =
			environment.API_BASE_URL + '/api/reports/InvoiceBundleReport';

		const content_ = JSON.stringify(request);

		const options_: any = {
			body: content_,
			observe: 'response',
			responseType: 'blob',
			headers: new HttpHeaders({
				'Content-Type': 'application/json',
				Accept: 'application/pdf',
			}),
		};

		return this.http
			.request('post', url_, options_)
			.pipe(
				_observableMergeMap((response_: any) => {
					return this.processInvoiceBundleReport(response_);
				})
			)
			.pipe(
				_observableCatch((response_: any) => {
					if (response_ instanceof HttpResponseBase) {
						try {
							return this.processInvoiceBundleReport(
								<any>response_
							);
						} catch (e) {
							return <Observable<any>>(<any>_observableThrow(e));
						}
					} else {
						return <Observable<any>>(
							(<any>_observableThrow(response_))
						);
					}
				})
			);
	}

	protected processInvoiceBundleReport(
		response: HttpResponseBase
	): Observable<any> {
		const status = response.status;
		const responseBlob =
			response instanceof HttpResponse
				? response.body
				: (<any>response).error instanceof Blob
				? (<any>response).error
				: undefined;

		const _headers: any = {};
		if (response.headers) {
			for (const key of response.headers.keys()) {
				_headers[key] = response.headers.get(key);
			}
		}
		if (status === 200) {
			return _observableOf(responseBlob);
		} else if (status !== 200 && status !== 204) {
			return blobToText(responseBlob).pipe(
				_observableMergeMap((_responseText) => {
					return throwException(
						'An unexpected server error occurred.',
						status,
						_responseText,
						_headers
					);
				})
			);
		}
		return _observableOf<any>(<any>null);
	}

	invoiceDataEntryReport(invoiceId: number): Observable<any> {
		let url_ =
			environment.API_BASE_URL +
			'/api/reports/InvoiceDataEntryReport/{invoiceId}';
		if (invoiceId === undefined || invoiceId === null || invoiceId === 0) {
			throw new Error("The parameter 'invoiceId' must be defined.");
		}
		url_ = url_.replace('{invoiceId}', encodeURIComponent('' + invoiceId));
		url_ = url_.replace(/[?&]$/, '');

		const options_: any = {
			observe: 'response',
			responseType: 'blob',
			headers: new HttpHeaders({
				'Content-Type': 'application/pdf',
				Accept: 'application/pdf',
			}),
		};

		return this.http
			.request('get', url_, options_)
			.pipe(
				_observableMergeMap((response_: any) => {
					return this.processInvoiceDataEntryReport(response_);
				})
			)
			.pipe(
				_observableCatch((response_: any) => {
					if (response_ instanceof HttpResponseBase) {
						try {
							return this.processInvoiceDataEntryReport(
								<any>response_
							);
						} catch (e) {
							return <Observable<any>>(<any>_observableThrow(e));
						}
					} else {
						return <Observable<any>>(
							(<any>_observableThrow(response_))
						);
					}
				})
			);
	}

	protected processInvoiceDataEntryReport(
		response: HttpResponseBase
	): Observable<any> {
		const status = response.status;
		const responseBlob =
			response instanceof HttpResponse
				? response.body
				: (<any>response).error instanceof Blob
				? (<any>response).error
				: undefined;

		const _headers: any = {};
		if (response.headers) {
			for (const key of response.headers.keys()) {
				_headers[key] = response.headers.get(key);
			}
		}
		if (status === 200) {
			return _observableOf(responseBlob);
		} else if (status !== 200 && status !== 204) {
			return blobToText(responseBlob).pipe(
				_observableMergeMap((_responseText) => {
					return throwException(
						'An unexpected server error occurred.',
						status,
						_responseText,
						_headers
					);
				})
			);
		}
		return _observableOf<any>(<any>null);
	}

	wcrReportA(invoiceCandidateId: number): Observable<any> {
		let url_ =
			environment.API_BASE_URL +
			'/api/reports/WcrReportA/{invoiceCandidateId}';
		if (invoiceCandidateId === undefined || invoiceCandidateId === null) {
			throw new Error(
				"The parameter 'invoiceCandidateId' must be defined."
			);
		}
		url_ = url_.replace(
			'{invoiceCandidateId}',
			encodeURIComponent('' + invoiceCandidateId)
		);
		url_ = url_.replace(/[?&]$/, '');

		const options_: any = {
			observe: 'response',
			responseType: 'blob',
			headers: new HttpHeaders({
				'Content-Type': 'application/pdf',
				Accept: 'application/pdf',
			}),
		};

		return this.http
			.request('get', url_, options_)
			.pipe(
				_observableMergeMap((response_: any) => {
					return this.processWcrReportA(response_);
				})
			)
			.pipe(
				_observableCatch((response_: any) => {
					if (response_ instanceof HttpResponseBase) {
						try {
							return this.processWcrReportA(<any>response_);
						} catch (e) {
							return <Observable<any>>(<any>_observableThrow(e));
						}
					} else {
						return <Observable<any>>(
							(<any>_observableThrow(response_))
						);
					}
				})
			);
	}

	protected processWcrReportA(response: HttpResponseBase): Observable<any> {
		const status = response.status;
		const responseBlob =
			response instanceof HttpResponse
				? response.body
				: (<any>response).error instanceof Blob
				? (<any>response).error
				: undefined;

		const _headers: any = {};
		if (response.headers) {
			for (const key of response.headers.keys()) {
				_headers[key] = response.headers.get(key);
			}
		}
		if (status === 200) {
			return _observableOf(responseBlob);
		} else if (status !== 200 && status !== 204) {
			return blobToText(responseBlob).pipe(
				_observableMergeMap((_responseText) => {
					return throwException(
						'An unexpected server error occurred.',
						status,
						_responseText,
						_headers
					);
				})
			);
		}
		return _observableOf<any>(<any>null);
	}

	wcrReportB(invoiceCandidateId: number): Observable<any> {
		let url_ =
			environment.API_BASE_URL +
			'/api/reports/WcrReportB/{invoiceCandidateId}';
		if (invoiceCandidateId === undefined || invoiceCandidateId === null) {
			throw new Error(
				"The parameter 'invoiceCandidateId' must be defined."
			);
		}
		url_ = url_.replace(
			'{invoiceCandidateId}',
			encodeURIComponent('' + invoiceCandidateId)
		);
		url_ = url_.replace(/[?&]$/, '');

		const options_: any = {
			observe: 'response',
			responseType: 'blob',
			headers: new HttpHeaders({
				'Content-Type': 'application/pdf',
				Accept: 'application/pdf',
			}),
		};

		return this.http
			.request('get', url_, options_)
			.pipe(
				_observableMergeMap((response_: any) => {
					return this.processWcrReportB(response_);
				})
			)
			.pipe(
				_observableCatch((response_: any) => {
					if (response_ instanceof HttpResponseBase) {
						try {
							return this.processWcrReportB(<any>response_);
						} catch (e) {
							return <Observable<any>>(<any>_observableThrow(e));
						}
					} else {
						return <Observable<any>>(
							(<any>_observableThrow(response_))
						);
					}
				})
			);
	}

	protected processWcrReportB(response: HttpResponseBase): Observable<any> {
		const status = response.status;
		const responseBlob =
			response instanceof HttpResponse
				? response.body
				: (<any>response).error instanceof Blob
				? (<any>response).error
				: undefined;

		const _headers: any = {};
		if (response.headers) {
			for (const key of response.headers.keys()) {
				_headers[key] = response.headers.get(key);
			}
		}
		if (status === 200) {
			return _observableOf(responseBlob);
		} else if (status !== 200 && status !== 204) {
			return blobToText(responseBlob).pipe(
				_observableMergeMap((_responseText) => {
					return throwException(
						'An unexpected server error occurred.',
						status,
						_responseText,
						_headers
					);
				})
			);
		}
		return _observableOf<any>(<any>null);
	}

	wcrReports(invoiceCandidateId: number): Observable<any> {
		let url_ =
			environment.API_BASE_URL +
			'/api/reports/WcrReports/{invoiceCandidateId}';
		if (invoiceCandidateId === undefined || invoiceCandidateId === null) {
			throw new Error(
				"The parameter 'invoiceCandidateId' must be defined."
			);
		}
		url_ = url_.replace(
			'{invoiceCandidateId}',
			encodeURIComponent('' + invoiceCandidateId)
		);
		url_ = url_.replace(/[?&]$/, '');

		const options_: any = {
			observe: 'response',
			responseType: 'blob',
			headers: new HttpHeaders({
				'Content-Type': 'application/pdf',
				Accept: 'application/pdf',
			}),
		};

		return this.http
			.request('get', url_, options_)
			.pipe(
				_observableMergeMap((response_: any) => {
					return this.processWcrReports(response_);
				})
			)
			.pipe(
				_observableCatch((response_: any) => {
					if (response_ instanceof HttpResponseBase) {
						try {
							return this.processWcrReports(<any>response_);
						} catch (e) {
							return <Observable<any>>(<any>_observableThrow(e));
						}
					} else {
						return <Observable<any>>(
							(<any>_observableThrow(response_))
						);
					}
				})
			);
	}

	protected processWcrReports(response: HttpResponseBase): Observable<any> {
		const status = response.status;
		const responseBlob =
			response instanceof HttpResponse
				? response.body
				: (<any>response).error instanceof Blob
				? (<any>response).error
				: undefined;

		const _headers: any = {};
		if (response.headers) {
			for (const key of response.headers.keys()) {
				_headers[key] = response.headers.get(key);
			}
		}
		if (status === 200) {
			return _observableOf(responseBlob);
		} else if (status !== 200 && status !== 204) {
			return blobToText(responseBlob).pipe(
				_observableMergeMap((_responseText) => {
					return throwException(
						'An unexpected server error occurred.',
						status,
						_responseText,
						_headers
					);
				})
			);
		}
		return _observableOf<any>(<any>null);
	}

	dwrReport(dwrId: number): Observable<any> {
		let url_ = environment.API_BASE_URL + '/api/reports/DwrReport/{dwrId}';
		if (dwrId === undefined || dwrId === null) {
			throw new Error("The parameter 'dwrId' must be defined.");
		}
		url_ = url_.replace('{dwrId}', encodeURIComponent('' + dwrId));
		url_ = url_.replace(/[?&]$/, '');

		const options_: any = {
			observe: 'response',
			responseType: 'blob',
			headers: new HttpHeaders({
				'Content-Type': 'application/pdf',
				Accept: 'application/pdf',
			}),
		};

		return this.http
			.request('get', url_, options_)
			.pipe(
				_observableMergeMap((response_: any) => {
					return this.processDwrReport(response_);
				}),
				map((res: any) => URL.createObjectURL(res)),
				tap((fileURL: string) => window.open(fileURL, '_blank'))
			)
			.pipe(
				_observableCatch((response_: any) => {
					if (response_ instanceof HttpResponseBase) {
						try {
							return this.processDwrReport(<any>response_);
						} catch (e) {
							return <Observable<any>>(<any>_observableThrow(e));
						}
					} else {
						return <Observable<any>>(
							(<any>_observableThrow(response_))
						);
					}
				})
			);
	}

	protected processDwrReport(response: HttpResponseBase): Observable<any> {
		const status = response.status;
		const responseBlob =
			response instanceof HttpResponse
				? response.body
				: (<any>response).error instanceof Blob
				? (<any>response).error
				: undefined;

		const _headers: any = {};
		if (response.headers) {
			for (const key of response.headers.keys()) {
				_headers[key] = response.headers.get(key);
			}
		}
		if (status === 200) {
			return _observableOf(responseBlob);
		} else if (status !== 200 && status !== 204) {
			return blobToText(responseBlob).pipe(
				_observableMergeMap((_responseText) => {
					return throwException(
						'An unexpected server error occurred.',
						status,
						_responseText,
						_headers
					);
				})
			);
		}
		return _observableOf<any>(<any>null);
	}

	jobSetupReport(jsId: number): Observable<any> {
		let url_ = environment.API_BASE_URL + '/api/reports/JobSetupReport/{jsId}';
		if (jsId === undefined || jsId === null) {
			throw new Error("The parameter 'jsId' must be defined.");
		}
		url_ = url_.replace('{jsId}', encodeURIComponent('' + jsId));
		url_ = url_.replace(/[?&]$/, '');

		const options_: any = {
			observe: 'response',
			responseType: 'blob',
			headers: new HttpHeaders({
				'Content-Type': 'application/pdf',
				Accept: 'application/pdf',
			}),
		};

		return this.http
			.request('get', url_, options_)
			.pipe(
				_observableMergeMap((response_: any) => {
					return this.processJobSetupReport(response_);
				}),
				map((res: any) => URL.createObjectURL(res)),
				tap((fileURL: string) => window.open(fileURL, '_blank'))
			)
			.pipe(
				_observableCatch((response_: any) => {
					if (response_ instanceof HttpResponseBase) {
						try {
							return this.processJobSetupReport(<any>response_);
						} catch (e) {
							return <Observable<any>>(<any>_observableThrow(e));
						}
					} else {
						return <Observable<any>>(
							(<any>_observableThrow(response_))
						);
					}
				})
			);
	}

	protected processJobSetupReport(response: HttpResponseBase): Observable<any> {
		const status = response.status;
		const responseBlob =
			response instanceof HttpResponse
				? response.body
				: (<any>response).error instanceof Blob
				? (<any>response).error
				: undefined;

		const _headers: any = {};
		if (response.headers) {
			for (const key of response.headers.keys()) {
				_headers[key] = response.headers.get(key);
			}
		}
		if (status === 200) {
			return _observableOf(responseBlob);
		} else if (status !== 200 && status !== 204) {
			return blobToText(responseBlob).pipe(
				_observableMergeMap((_responseText) => {
					return throwException(
						'An unexpected server error occurred.',
						status,
						_responseText,
						_headers
					);
				})
			);
		}
		return _observableOf<any>(<any>null);
	}

	utilityJobSetupReport(utilityJobSetupId: number): Observable<any> {
		let url_ =
			environment.API_BASE_URL +
			'/api/reports/UtilityJobSetupReport/{utilityJobSetupId}';
		if (utilityJobSetupId === undefined || utilityJobSetupId === null) {
			throw new Error(
				"The parameter 'utilityJobSetupId' must be defined."
			);
		}
		url_ = url_.replace(
			'{utilityJobSetupId}',
			encodeURIComponent('' + utilityJobSetupId)
		);
		url_ = url_.replace(/[?&]$/, '');

		const options_: any = {
			observe: 'response',
			responseType: 'blob',
			headers: new HttpHeaders({
				'Content-Type': 'application/pdf',
				Accept: 'application/pdf',
			}),
		};

		return this.http
			.request('get', url_, options_)
			.pipe(
				_observableMergeMap((response_: any) => {
					return this.processUtilityJobSetupReport(response_);
				}),
				map((res: any) => URL.createObjectURL(res)),
				tap((fileURL: string) => window.open(fileURL, '_blank'))
			)
			.pipe(
				_observableCatch((response_: any) => {
					if (response_ instanceof HttpResponseBase) {
						try {
							return this.processUtilityJobSetupReport(
								<any>response_
							);
						} catch (e) {
							return <Observable<any>>(<any>_observableThrow(e));
						}
					} else {
						return <Observable<any>>(
							(<any>_observableThrow(response_))
						);
					}
				})
			);
	}

	protected processUtilityJobSetupReport(
		response: HttpResponseBase
	): Observable<any> {
		const status = response.status;
		const responseBlob =
			response instanceof HttpResponse
				? response.body
				: (<any>response).error instanceof Blob
				? (<any>response).error
				: undefined;

		const _headers: any = {};
		if (response.headers) {
			for (const key of response.headers.keys()) {
				_headers[key] = response.headers.get(key);
			}
		}
		if (status === 200) {
			return _observableOf(responseBlob);
		} else if (status !== 200 && status !== 204) {
			return blobToText(responseBlob).pipe(
				_observableMergeMap((_responseText) => {
					return throwException(
						'An unexpected server error occurred.',
						status,
						_responseText,
						_headers
					);
				})
			);
		}
		return _observableOf<any>(<any>null);
	}

	utilityCoverSheetReport(utilityCoverSheetId: number): Observable<any> {
		let url_ =
			environment.API_BASE_URL +
			'/api/reports/UtilityCoverSheetReport/{utilityCoverSheetId}';
		if (utilityCoverSheetId === undefined || utilityCoverSheetId === null) {
			throw new Error(
				"The parameter 'utilityCoverSheetId' must be defined."
			);
		}
		url_ = url_.replace(
			'{utilityCoverSheetId}',
			encodeURIComponent('' + utilityCoverSheetId)
		);
		url_ = url_.replace(/[?&]$/, '');

		const options_: any = {
			observe: 'response',
			responseType: 'blob',
			headers: new HttpHeaders({
				'Content-Type': 'application/pdf',
				Accept: 'application/pdf',
			}),
		};

		return this.http
			.request('get', url_, options_)
			.pipe(
				_observableMergeMap((response_: any) => {
					return this.processUtilityCoverSheetReport(response_);
				}),
				map((res: any) => URL.createObjectURL(res)),
				tap((fileURL: string) => window.open(fileURL, '_blank'))
			)
			.pipe(
				_observableCatch((response_: any) => {
					if (response_ instanceof HttpResponseBase) {
						try {
							return this.processUtilityCoverSheetReport(
								<any>response_
							);
						} catch (e) {
							return <Observable<any>>(<any>_observableThrow(e));
						}
					} else {
						return <Observable<any>>(
							(<any>_observableThrow(response_))
						);
					}
				})
			);
	}

	protected processUtilityCoverSheetReport(
		response: HttpResponseBase
	): Observable<any> {
		const status = response.status;
		const responseBlob =
			response instanceof HttpResponse
				? response.body
				: (<any>response).error instanceof Blob
				? (<any>response).error
				: undefined;

		const _headers: any = {};
		if (response.headers) {
			for (const key of response.headers.keys()) {
				_headers[key] = response.headers.get(key);
			}
		}
		if (status === 200) {
			return _observableOf(responseBlob);
		} else if (status !== 200 && status !== 204) {
			return blobToText(responseBlob).pipe(
				_observableMergeMap((_responseText) => {
					return throwException(
						'An unexpected server error occurred.',
						status,
						_responseText,
						_headers
					);
				})
			);
		}
		return _observableOf<any>(<any>null);
	}

	dwrReports(args: DwrListArgs): Observable<any> {
		let url_ = environment.API_BASE_URL + '/api/reports/DwrReports';
		url_ = url_.replace(/[?&]$/, '');

		const content_ = JSON.stringify(args);

		const options_: any = {
			body: content_,
			observe: 'response',
			responseType: 'blob',
			headers: new HttpHeaders({
				'Content-Type': 'application/json',
				Accept: 'application/pdf',
			}),
		};

		return this.http
			.request('post', url_, options_)
			.pipe(
				_observableMergeMap((response_: any) => {
					return this.processDwrReports(response_);
				}),
				map((res: any) => URL.createObjectURL(res)),
				tap((fileURL: string) => window.open(fileURL, '_blank'))
			)
			.pipe(
				_observableCatch((response_: any) => {
					if (response_ instanceof HttpResponseBase) {
						try {
							return this.processDwrReports(<any>response_);
						} catch (e) {
							return <Observable<any>>(<any>_observableThrow(e));
						}
					} else {
						return <Observable<any>>(
							(<any>_observableThrow(response_))
						);
					}
				})
			);
	}

	protected processDwrReports(response: HttpResponseBase): Observable<any> {
		const status = response.status;
		const responseBlob =
			response instanceof HttpResponse
				? response.body
				: (<any>response).error instanceof Blob
				? (<any>response).error
				: undefined;

		const _headers: any = {};
		if (response.headers) {
			for (const key of response.headers.keys()) {
				_headers[key] = response.headers.get(key);
			}
		}
		if (status === 200) {
			return _observableOf(responseBlob);
		} else if (status !== 200 && status !== 204) {
			return blobToText(responseBlob).pipe(
				_observableMergeMap((_responseText) => {
					return throwException(
						'An unexpected server error occurred.',
						status,
						_responseText,
						_headers
					);
				})
			);
		}
		return _observableOf<any>(<any>null);
	}

	/**
	 * @return OK
	 */
	dprReport(dwrId: number): Observable<any> {
		let url_ = environment.API_BASE_URL + '/api/reports/DprReport/{dwrId}';
		if (dwrId === undefined || dwrId === null)
			throw new Error("The parameter 'dwrId' must be defined.");
		url_ = url_.replace('{dwrId}', encodeURIComponent('' + dwrId));
		url_ = url_.replace(/[?&]$/, '');

		let options_: any = {
			observe: 'response',
			responseType: 'blob',
			headers: new HttpHeaders({
				Accept: 'application/json',
			}),
		};

		return this.http
			.request('get', url_, options_)
			.pipe(
				_observableMergeMap((response_: any) => {
					return this.processDprReport(response_);
				})
			)
			.pipe(
				_observableCatch((response_: any) => {
					if (response_ instanceof HttpResponseBase) {
						try {
							return this.processDprReport(<any>response_);
						} catch (e) {
							return <Observable<any>>(<any>_observableThrow(e));
						}
					} else
						return <Observable<any>>(
							(<any>_observableThrow(response_))
						);
				})
			);
	}

	protected processDprReport(response: HttpResponseBase): Observable<any> {
		const status = response.status;
		const responseBlob =
			response instanceof HttpResponse
				? response.body
				: (<any>response).error instanceof Blob
				? (<any>response).error
				: undefined;

		let _headers: any = {};
		if (response.headers) {
			for (let key of response.headers.keys()) {
				_headers[key] = response.headers.get(key);
			}
		}
		if (status === 200) {
			return _observableOf(responseBlob);
		} else if (status !== 200 && status !== 204) {
			return blobToText(responseBlob).pipe(
				_observableMergeMap((_responseText) => {
					return throwException(
						'An unexpected server error occurred.',
						status,
						_responseText,
						_headers
					);
				})
			);
		}
		return _observableOf<any>(<any>null);
	}

	billableJobListCsv(args: BillableJobListArgs): Observable<any> {
		let url_ =
			environment.API_BASE_URL + '/api/reports/BillableJobsListCsv';
		url_ = url_.replace(/[?&]$/, '');

		const content_ = JSON.stringify(args);

		const options_: any = {
			body: content_,
			observe: 'response',
			responseType: 'blob',
			headers: new HttpHeaders({
				'Content-Type': 'application/json',
				Accept: 'application/vnd.openxmlformatsofficedocument.spreadsheetml.sheet',
			}),
		};

		return this.http
			.request('post', url_, options_)
			.pipe(
				_observableMergeMap((response_: any) => {
					return this.processBillableJobListCsv(response_);
				}),
				map((response) => {
					const blob = new Blob([response], {
						type: 'application/vnd.oasis.opendocument.spreadsheet',
					});
					const a = document.createElement('a');
					a.href = URL.createObjectURL(blob);
					a.download = `${moment
						.utc()
						.local()
						.format('YYYY-MM-DD_HH-mm')}_Invoice_List_Export.csv`;
					a.click();
				})
			)
			.pipe(
				_observableCatch((response_: any) => {
					if (response_ instanceof HttpResponseBase) {
						try {
							return this.processBillableJobListCsv(
								<any>response_
							);
						} catch (e) {
							return <Observable<any>>(<any>_observableThrow(e));
						}
					} else {
						return <Observable<any>>(
							(<any>_observableThrow(response_))
						);
					}
				})
			);
	}

	protected processBillableJobListCsv(
		response: HttpResponseBase
	): Observable<any> {
		const status = response.status;
		const responseBlob =
			response instanceof HttpResponse
				? response.body
				: (<any>response).error instanceof Blob
				? (<any>response).error
				: undefined;

		const _headers: any = {};
		if (response.headers) {
			for (const key of response.headers.keys()) {
				_headers[key] = response.headers.get(key);
			}
		}
		if (status === 200) {
			return _observableOf(responseBlob);
		} else if (status !== 200 && status !== 204) {
			return blobToText(responseBlob).pipe(
				_observableMergeMap((_responseText) => {
					return throwException(
						'An unexpected server error occurred.',
						status,
						_responseText,
						_headers
					);
				})
			);
		}
		return _observableOf<any>(<any>null);
	}

	payrollMonthlyReportCsv(args: PayrollMonthlyReportArgs): Observable<any> {
		let url_ =
			environment.API_BASE_URL +
			'/api/reports/PayrollMonthlyReportCsv/{jobId}/{startDate}';
		if (args.startDate === undefined || args.startDate === null) {
			throw new Error("The parameter 'startDate' must be defined.");
		}
		if (args.jobId === undefined || args.jobId === null) {
			throw new Error("The parameter 'jobId' must be defined.");
		}
		url_ = url_.replace(
			'{startDate}',
			encodeURIComponent('' + moment(args.startDate).format('YYYY-MM-DD'))
		);
		url_ = url_.replace('{jobId}', encodeURIComponent('' + args.jobId));
		url_ = url_.replace(/[?&]$/, '');

		const options_: any = {
			observe: 'response',
			responseType: 'blob',
			headers: new HttpHeaders({
				Accept: 'application/json',
			}),
		};

		if (!args.filename) {
			args.filename = `${moment
				.utc()
				.local()
				.format('YYYY-MM-DD_HH-mm')}_Payroll_Monthly_Report.csv`;
		}

		return this.http
			.request('get', url_, options_)
			.pipe(
				_observableMergeMap((response_: any) => {
					return this.processPayrollMonthlyReportCsv(response_);
				}),
				map((response) => {
					const blob = new Blob([response], {
						type: 'application/vnd.oasis.opendocument.spreadsheet',
					});
					const a = document.createElement('a');
					a.href = URL.createObjectURL(blob);
					a.download = args.filename;
					a.click();
				})
			)
			.pipe(
				_observableCatch((response_: any) => {
					if (response_ instanceof HttpResponseBase) {
						try {
							return this.processPayrollMonthlyReportCsv(
								<any>response_
							);
						} catch (e) {
							return <Observable<any>>(<any>_observableThrow(e));
						}
					} else {
						return <Observable<any>>(
							(<any>_observableThrow(response_))
						);
					}
				})
			);
	}

	protected processPayrollMonthlyReportCsv(
		response: HttpResponseBase
	): Observable<any> {
		const status = response.status;
		const responseBlob =
			response instanceof HttpResponse
				? response.body
				: (<any>response).error instanceof Blob
				? (<any>response).error
				: undefined;

		const _headers: any = {};
		if (response.headers) {
			for (const key of response.headers.keys()) {
				_headers[key] = response.headers.get(key);
			}
		}
		if (status === 200) {
			return _observableOf(responseBlob);
		} else if (status !== 200 && status !== 204) {
			return blobToText(responseBlob).pipe(
				_observableMergeMap((_responseText) => {
					return throwException(
						'An unexpected server error occurred.',
						status,
						_responseText,
						_headers
					);
				})
			);
		}
		return _observableOf<any>(<any>null);
	}

	invoiceSummaryCsv(request: InvoiceSummaryArgs): Observable<any> {
		let url_ = environment.API_BASE_URL + '/api/reports/InvoiceSummaryCsv';
		url_ = url_.replace(/[?&]$/, '');

		const content_ = JSON.stringify(request);

		let options_: any = {
			body: content_,
			observe: 'response',
			responseType: 'blob',
			headers: new HttpHeaders({
				'Content-Type': 'application/json',
				Accept: 'application/json',
			}),
		};

		return this.http
			.request('post', url_, options_)
			.pipe(
				_observableMergeMap((response_: any) => {
					return this.processInvoiceSummaryCsv(response_);
				}),
				map((response) => {
					const blob = new Blob([response], {
						type: 'application/vnd.oasis.opendocument.spreadsheet',
					});
					const a = document.createElement('a');
					a.href = URL.createObjectURL(blob);
					a.download = request.filename;
					a.click();
				})
			)
			.pipe(
				_observableCatch((response_: any) => {
					if (response_ instanceof HttpResponseBase) {
						try {
							return this.processInvoiceSummaryCsv(
								<any>response_
							);
						} catch (e) {
							return <Observable<any>>(<any>_observableThrow(e));
						}
					} else {
						return <Observable<any>>(
							(<any>_observableThrow(response_))
						);
					}
				})
			);
	}

	protected processInvoiceSummaryCsv(
		response: HttpResponseBase
	): Observable<any> {
		const status = response.status;
		const responseBlob =
			response instanceof HttpResponse
				? response.body
				: (<any>response).error instanceof Blob
				? (<any>response).error
				: undefined;

		const _headers: any = {};
		if (response.headers) {
			for (const key of response.headers.keys()) {
				_headers[key] = response.headers.get(key);
			}
		}
		if (status === 200) {
			return _observableOf(responseBlob);
		} else if (status !== 200 && status !== 204) {
			return blobToText(responseBlob).pipe(
				_observableMergeMap((_responseText) => {
					return throwException(
						'An unexpected server error occurred.',
						status,
						_responseText,
						_headers
					);
				})
			);
		}
		return _observableOf<any>(<any>null);
	}

	dwrsForInvoice(invoiceId: number): Observable<any> {
		let url_ =
			environment.API_BASE_URL +
			'/api/reports/DwrsForInvoice/{invoiceId}';
		if (invoiceId === undefined || invoiceId === null || invoiceId === 0) {
			throw new Error("The parameter 'invoiceId' must be defined.");
		}
		url_ = url_.replace('{invoiceId}', encodeURIComponent('' + invoiceId));
		url_ = url_.replace(/[?&]$/, '');

		const options_: any = {
			observe: 'response',
			responseType: 'blob',
			headers: new HttpHeaders({
				'Content-Type': 'application/pdf',
				Accept: 'application/pdf',
			}),
		};

		return this.http
			.request('get', url_, options_)
			.pipe(
				_observableMergeMap((response_: any) => {
					return this.processDwrsForInvoice(response_);
				})
			)
			.pipe(
				_observableCatch((response_: any) => {
					if (response_ instanceof HttpResponseBase) {
						try {
							return this.processDwrsForInvoice(<any>response_);
						} catch (e) {
							return <Observable<any>>(<any>_observableThrow(e));
						}
					} else {
						return <Observable<any>>(
							(<any>_observableThrow(response_))
						);
					}
				})
			);
	}

	protected processDwrsForInvoice(
		response: HttpResponseBase
	): Observable<any> {
		const status = response.status;
		const responseBlob =
			response instanceof HttpResponse
				? response.body
				: (<any>response).error instanceof Blob
				? (<any>response).error
				: undefined;

		const _headers: any = {};
		if (response.headers) {
			for (const key of response.headers.keys()) {
				_headers[key] = response.headers.get(key);
			}
		}
		if (status === 200) {
			return _observableOf(responseBlob);
		} else if (status !== 200 && status !== 204) {
			return blobToText(responseBlob).pipe(
				_observableMergeMap((_responseText) => {
					return throwException(
						'An unexpected server error occurred.',
						status,
						_responseText,
						_headers
					);
				})
			);
		}
		return _observableOf<any>(<any>null);
	}

	bundledInvoiceAndDwrs(invoiceId: number): Observable<any> {
		let url_ =
			environment.API_BASE_URL +
			'/api/reports/BundledInvoiceAndDwrs/{invoiceId}';
		if (invoiceId === undefined || invoiceId === null || invoiceId === 0) {
			throw new Error("The parameter 'invoiceId' must be defined.");
		}
		url_ = url_.replace('{invoiceId}', encodeURIComponent('' + invoiceId));
		url_ = url_.replace(/[?&]$/, '');

		const options_: any = {
			observe: 'response',
			responseType: 'blob',
			headers: new HttpHeaders({
				'Content-Type': 'application/pdf',
				Accept: 'application/pdf',
			}),
		};

		return this.http
			.request('get', url_, options_)
			.pipe(
				_observableMergeMap((response_: any) => {
					return this.processBundledInvoiceAndDwrs(response_);
				})
			)
			.pipe(
				_observableCatch((response_: any) => {
					if (response_ instanceof HttpResponseBase) {
						try {
							return this.processBundledInvoiceAndDwrs(
								<any>response_
							);
						} catch (e) {
							return <Observable<any>>(<any>_observableThrow(e));
						}
					} else {
						return <Observable<any>>(
							(<any>_observableThrow(response_))
						);
					}
				})
			);
	}

	protected processBundledInvoiceAndDwrs(
		response: HttpResponseBase
	): Observable<any> {
		const status = response.status;
		const responseBlob =
			response instanceof HttpResponse
				? response.body
				: (<any>response).error instanceof Blob
				? (<any>response).error
				: undefined;

		const _headers: any = {};
		if (response.headers) {
			for (const key of response.headers.keys()) {
				_headers[key] = response.headers.get(key);
			}
		}
		if (status === 200) {
			return _observableOf(responseBlob);
		} else if (status !== 200 && status !== 204) {
			return blobToText(responseBlob).pipe(
				_observableMergeMap((_responseText) => {
					return throwException(
						'An unexpected server error occurred.',
						status,
						_responseText,
						_headers
					);
				})
			);
		}
		return _observableOf<any>(<any>null);
	}

	/**
	 * @return OK
	 */
	invoiceArtifacts(invoiceId: number): Observable<any> {
		let url_ =
			environment.API_BASE_URL +
			'/api/reports/InvoiceArtifacts/{invoiceId}';
		if (invoiceId === undefined || invoiceId === null || invoiceId === 0) {
			throw new Error("The parameter 'invoiceId' must be defined.");
		}
		url_ = url_.replace('{invoiceId}', encodeURIComponent('' + invoiceId));
		url_ = url_.replace(/[?&]$/, '');

		const options_: any = {
			observe: 'response',
			responseType: 'blob',
			headers: new HttpHeaders({
				'Content-Type': 'application/pdf',
				Accept: 'application/pdf',
			}),
		};

		return this.http
			.request('get', url_, options_)
			.pipe(
				_observableMergeMap((response_: any) => {
					return this.processInvoiceW9AndDwrs(response_);
				})
			)
			.pipe(
				_observableCatch((response_: any) => {
					if (response_ instanceof HttpResponseBase) {
						try {
							return this.processInvoiceW9AndDwrs(<any>response_);
						} catch (e) {
							return <Observable<any>>(<any>_observableThrow(e));
						}
					} else {
						return <Observable<any>>(
							(<any>_observableThrow(response_))
						);
					}
				})
			);
	}

	protected processInvoiceW9AndDwrs(
		response: HttpResponseBase
	): Observable<any> {
		const status = response.status;
		const responseBlob =
			response instanceof HttpResponse
				? response.body
				: (<any>response).error instanceof Blob
				? (<any>response).error
				: undefined;

		const _headers: any = {};
		if (response.headers) {
			for (const key of response.headers.keys()) {
				_headers[key] = response.headers.get(key);
			}
		}
		if (status === 200) {
			return _observableOf(responseBlob);
		} else if (status !== 200 && status !== 204) {
			return blobToText(responseBlob).pipe(
				_observableMergeMap((_responseText) => {
					return throwException(
						'An unexpected server error occurred.',
						status,
						_responseText,
						_headers
					);
				})
			);
		}
		return _observableOf<any>(<any>null);
	}

	/**
	 * @return OK
	 */
	getAndCacheInvoiceArtifacts(invoiceCandidateId: number): Observable<any> {
		let url_ =
			environment.API_BASE_URL +
			'/api/reports/InvoiceArtifacts/{invoiceCandidateId}';
		if (invoiceCandidateId === undefined || invoiceCandidateId === null) {
			throw new Error(
				"The parameter 'invoiceCandidateId' must be defined."
			);
		}
		url_ = url_.replace(
			'{invoiceCandidateId}',
			encodeURIComponent('' + invoiceCandidateId)
		);
		url_ = url_.replace(/[?&]$/, '');

		const options_: any = {
			observe: 'response',
			responseType: 'blob',
			headers: new HttpHeaders({
				'Content-Type': 'application/pdf',
				Accept: 'application/pdf',
			}),
		};

		return this.http
			.request('post', url_, options_)
			.pipe(
				_observableMergeMap((response_: any) => {
					return this.processGetAndCacheInvoiceArtifacts(response_);
				})
			)
			.pipe(
				_observableCatch((response_: any) => {
					if (response_ instanceof HttpResponseBase) {
						try {
							return this.processGetAndCacheInvoiceArtifacts(
								<any>response_
							);
						} catch (e) {
							return <Observable<any>>(<any>_observableThrow(e));
						}
					} else {
						return <Observable<any>>(
							(<any>_observableThrow(response_))
						);
					}
				})
			);
	}

	protected processGetAndCacheInvoiceArtifacts(
		response: HttpResponseBase
	): Observable<any> {
		const status = response.status;
		const responseBlob =
			response instanceof HttpResponse
				? response.body
				: (<any>response).error instanceof Blob
				? (<any>response).error
				: undefined;

		const _headers: any = {};
		if (response.headers) {
			for (const key of response.headers.keys()) {
				_headers[key] = response.headers.get(key);
			}
		}
		if (status === 200) {
			return _observableOf(responseBlob);
		} else if (status !== 200 && status !== 204) {
			return blobToText(responseBlob).pipe(
				_observableMergeMap((_responseText) => {
					return throwException(
						'An unexpected server error occurred.',
						status,
						_responseText,
						_headers
					);
				})
			);
		}
		return _observableOf<any>(<any>null);
	}

	payrollBatchReport(batchId: number): Observable<any> {
		let url_ =
			environment.API_BASE_URL +
			'/api/reports/PayrollBatchReport/{batchId}';
		if (batchId === undefined || batchId === null || batchId === 0) {
			throw new Error("The parameter 'batchId' must be defined.");
		}
		url_ = url_.replace('{batchId}', encodeURIComponent('' + batchId));
		url_ = url_.replace(/[?&]$/, '');

		const options_: any = {
			observe: 'response',
			responseType: 'blob',
			headers: new HttpHeaders({
				'Content-Type': 'application/pdf',
				Accept: 'application/pdf',
			}),
		};

		return this.http
			.request('get', url_, options_)
			.pipe(
				_observableMergeMap((response_: any) => {
					return this.processPayrollBatchReport(response_);
				}),
				map((res: any) => URL.createObjectURL(res)),
				tap((fileURL: string) => window.open(fileURL, '_blank'))
			)
			.pipe(
				_observableCatch((response_: any) => {
					if (response_ instanceof HttpResponseBase) {
						try {
							return this.processPayrollBatchReport(
								<any>response_
							);
						} catch (e) {
							return <Observable<any>>(<any>_observableThrow(e));
						}
					} else {
						return <Observable<any>>(
							(<any>_observableThrow(response_))
						);
					}
				})
			);
	}

	protected processPayrollBatchReport(
		response: HttpResponseBase
	): Observable<any> {
		const status = response.status;
		const responseBlob =
			response instanceof HttpResponse
				? response.body
				: (<any>response).error instanceof Blob
				? (<any>response).error
				: undefined;

		const _headers: any = {};
		if (response.headers) {
			for (const key of response.headers.keys()) {
				_headers[key] = response.headers.get(key);
			}
		}
		if (status === 200) {
			return _observableOf(responseBlob);
		} else if (status !== 200 && status !== 204) {
			return blobToText(responseBlob).pipe(
				_observableMergeMap((_responseText) => {
					return throwException(
						'An unexpected server error occurred.',
						status,
						_responseText,
						_headers
					);
				})
			);
		}
		return _observableOf<any>(<any>null);
	}

	originalPaychecksReport(batchId: number): Observable<any> {
		let url_ =
			environment.API_BASE_URL +
			'/api/reports/OriginalPaychecksReport/{batchId}';
		if (batchId === undefined || batchId === null || batchId === 0) {
			throw new Error("The parameter 'batchId' must be defined.");
		}
		url_ = url_.replace('{batchId}', encodeURIComponent('' + batchId));
		url_ = url_.replace(/[?&]$/, '');

		const options_: any = {
			observe: 'response',
			responseType: 'blob',
			headers: new HttpHeaders({
				'Content-Type': 'application/pdf',
				Accept: 'application/pdf',
			}),
		};

		return this.http
			.request('get', url_, options_)
			.pipe(
				_observableMergeMap((response_: any) => {
					return this.processOriginalPaychecksReport(response_);
				})
			)
			.pipe(
				_observableCatch((response_: any) => {
					if (response_ instanceof HttpResponseBase) {
						try {
							return this.processOriginalPaychecksReport(
								<any>response_
							);
						} catch (e) {
							return <Observable<any>>(<any>_observableThrow(e));
						}
					} else {
						return <Observable<any>>(
							(<any>_observableThrow(response_))
						);
					}
				})
			);
	}

	protected processOriginalPaychecksReport(
		response: HttpResponseBase
	): Observable<any> {
		const status = response.status;
		const responseBlob =
			response instanceof HttpResponse
				? response.body
				: (<any>response).error instanceof Blob
				? (<any>response).error
				: undefined;

		const _headers: any = {};
		if (response.headers) {
			for (const key of response.headers.keys()) {
				_headers[key] = response.headers.get(key);
			}
		}
		if (status === 200) {
			return _observableOf(responseBlob);
		} else if (status !== 200 && status !== 204) {
			return blobToText(responseBlob).pipe(
				_observableMergeMap((_responseText) => {
					return throwException(
						'An unexpected server error occurred.',
						status,
						_responseText,
						_headers
					);
				})
			);
		}
		return _observableOf<any>(<any>null);
	}

	updatedPaychecksReport(batchId: number): Observable<any> {
		let url_ =
			environment.API_BASE_URL +
			'/api/reports/UpdatedPaychecksReport/{batchId}';
		if (batchId === undefined || batchId === null || batchId === 0) {
			throw new Error("The parameter 'batchId' must be defined.");
		}
		url_ = url_.replace('{batchId}', encodeURIComponent('' + batchId));
		url_ = url_.replace(/[?&]$/, '');

		const options_: any = {
			observe: 'response',
			responseType: 'blob',
			headers: new HttpHeaders({
				'Content-Type': 'application/pdf',
				Accept: 'application/pdf',
			}),
		};

		return this.http
			.request('get', url_, options_)
			.pipe(
				_observableMergeMap((response_: any) => {
					return this.processUpdatedPaychecksReport(response_);
				})
			)
			.pipe(
				_observableCatch((response_: any) => {
					if (response_ instanceof HttpResponseBase) {
						try {
							return this.processUpdatedPaychecksReport(
								<any>response_
							);
						} catch (e) {
							return <Observable<any>>(<any>_observableThrow(e));
						}
					} else {
						return <Observable<any>>(
							(<any>_observableThrow(response_))
						);
					}
				})
			);
	}

	protected processUpdatedPaychecksReport(
		response: HttpResponseBase
	): Observable<any> {
		const status = response.status;
		const responseBlob =
			response instanceof HttpResponse
				? response.body
				: (<any>response).error instanceof Blob
				? (<any>response).error
				: undefined;

		const _headers: any = {};
		if (response.headers) {
			for (const key of response.headers.keys()) {
				_headers[key] = response.headers.get(key);
			}
		}
		if (status === 200) {
			return _observableOf(responseBlob);
		} else if (status !== 200 && status !== 204) {
			return blobToText(responseBlob).pipe(
				_observableMergeMap((_responseText) => {
					return throwException(
						'An unexpected server error occurred.',
						status,
						_responseText,
						_headers
					);
				})
			);
		}
		return _observableOf<any>(<any>null);
	}
	getSubInvoice(batchId: number): Observable<any> {
		let url_ =
			environment.API_BASE_URL +
			'/api/reports/SubContractorInvoice/{batchId}';
		if (batchId === undefined || batchId === null || batchId === 0) {
			throw new Error("The parameter 'batchId' must be defined.");
		}
		url_ = url_.replace('{batchId}', encodeURIComponent('' + batchId));
		url_ = url_.replace(/[?&]$/, '');

		const options_: any = {
			observe: 'response',
			responseType: 'blob',
			headers: new HttpHeaders({
				'Content-Type': 'application/pdf',
				Accept: 'application/pdf',
			}),
		};

		return this.http
			.request('get', url_, options_)
			.pipe(
				_observableMergeMap((response_: any) => {
					return this.processGetSubInvoice(response_);
				})
			)
			.pipe(
				_observableCatch((response_: any) => {
					if (response_ instanceof HttpResponseBase) {
						try {
							return this.processGetSubInvoice(<any>response_);
						} catch (e) {
							return <Observable<any>>(<any>_observableThrow(e));
						}
					} else {
						return <Observable<any>>(
							(<any>_observableThrow(response_))
						);
					}
				})
			);
	}

	protected processGetSubInvoice(
		response: HttpResponseBase
	): Observable<any> {
		const status = response.status;
		const responseBlob =
			response instanceof HttpResponse
				? response.body
				: (<any>response).error instanceof Blob
				? (<any>response).error
				: undefined;

		const _headers: any = {};
		if (response.headers) {
			for (const key of response.headers.keys()) {
				_headers[key] = response.headers.get(key);
			}
		}
		if (status === 200) {
			return _observableOf(responseBlob);
		} else if (status !== 200 && status !== 204) {
			return blobToText(responseBlob).pipe(
				_observableMergeMap((_responseText) => {
					return throwException(
						'An unexpected server error occurred.',
						status,
						_responseText,
						_headers
					);
				})
			);
		}
		return _observableOf<any>(<any>null);
	}

	getZippedSubInvoices(batchId: number): Observable<any> {
		let url_ =
			environment.API_BASE_URL +
			'/api/reports/ZippedSubInvoices/{batchId}';
		if (batchId === undefined || batchId === null) {
			throw new Error("The parameter 'batchId' must be defined.");
		}
		url_ = url_.replace('{batchId}', encodeURIComponent('' + batchId));
		url_ = url_.replace(/[?&]$/, '');

		const options_: any = {
			observe: 'response',
			responseType: 'blob',
			headers: new HttpHeaders({
				'Content-Type': 'application/json',
				Accept: 'application/zip',
			}),
		};

		return this.http
			.request('get', url_, options_)
			.pipe(
				_observableMergeMap((response_: any) => {
					return this.processGetZippedSubInvoices(response_);
				}),
				map((response) => {
					const blob = new Blob([response], {
						type: 'application/octet-stream',
					});
					const a = document.createElement('a');
					a.href = URL.createObjectURL(blob);
					a.download = `${moment
						.utc()
						.local()
						.format('YYYY-MM-DD_HH-mm')}_Contractor_Invoices.zip`;
					a.click();
				})
			)
			.pipe(
				_observableCatch((response_: any) => {
					if (response_ instanceof HttpResponseBase) {
						try {
							return this.processGetZippedSubInvoices(
								<any>response_
							);
						} catch (e) {
							return <Observable<any>>(<any>_observableThrow(e));
						}
					} else {
						return <Observable<any>>(
							(<any>_observableThrow(response_))
						);
					}
				})
			);
	}

	protected processGetZippedSubInvoices(
		response: HttpResponseBase
	): Observable<any> {
		const status = response.status;
		const responseBlob =
			response instanceof HttpResponse
				? response.body
				: (<any>response).error instanceof Blob
				? (<any>response).error
				: undefined;

		const _headers: any = {};
		if (response.headers) {
			for (const key of response.headers.keys()) {
				_headers[key] = response.headers.get(key);
			}
		}
		if (status === 200) {
			return _observableOf(responseBlob);
		} else if (status !== 200 && status !== 204) {
			return blobToText(responseBlob).pipe(
				_observableMergeMap((_responseText) => {
					return throwException(
						'An unexpected server error occurred.',
						status,
						_responseText,
						_headers
					);
				})
			);
		}
		return _observableOf<any>(<any>null);
	}

	getZippedOriginalAndUpdatedTimesheets(batchId: number): Observable<any> {
		let url_ =
			environment.API_BASE_URL +
			'/api/reports/ZippedOriginalAndUpdatedTimesheets/{batchId}';
		if (batchId === undefined || batchId === null) {
			throw new Error("The parameter 'batchId' must be defined.");
		}
		url_ = url_.replace('{batchId}', encodeURIComponent('' + batchId));
		url_ = url_.replace(/[?&]$/, '');

		const options_: any = {
			observe: 'response',
			responseType: 'blob',
			headers: new HttpHeaders({
				'Content-Type': 'application/json',
				Accept: 'application/zip',
			}),
		};

		return this.http
			.request('get', url_, options_)
			.pipe(
				_observableMergeMap((response_: any) => {
					return this.processGetZippedOriginalAndUpdatedTimesheets(
						response_
					);
				}),
				map((response) => {
					const blob = new Blob([response], {
						type: 'application/octet-stream',
					});
					const a = document.createElement('a');
					a.href = URL.createObjectURL(blob);
					a.download = `${moment
						.utc()
						.local()
						.format(
							'YYYY-MM-DD_HH-mm'
						)}_Original_Updated_Timesheets.zip`;
					a.click();
				})
			)
			.pipe(
				_observableCatch((response_: any) => {
					if (response_ instanceof HttpResponseBase) {
						try {
							return this.processGetZippedOriginalAndUpdatedTimesheets(
								<any>response_
							);
						} catch (e) {
							return <Observable<any>>(<any>_observableThrow(e));
						}
					} else {
						return <Observable<any>>(
							(<any>_observableThrow(response_))
						);
					}
				})
			);
	}

	protected processGetZippedOriginalAndUpdatedTimesheets(
		response: HttpResponseBase
	): Observable<any> {
		const status = response.status;
		const responseBlob =
			response instanceof HttpResponse
				? response.body
				: (<any>response).error instanceof Blob
				? (<any>response).error
				: undefined;

		const _headers: any = {};
		if (response.headers) {
			for (const key of response.headers.keys()) {
				_headers[key] = response.headers.get(key);
			}
		}
		if (status === 200) {
			return _observableOf(responseBlob);
		} else if (status !== 200 && status !== 204) {
			return blobToText(responseBlob).pipe(
				_observableMergeMap((_responseText) => {
					return throwException(
						'An unexpected server error occurred.',
						status,
						_responseText,
						_headers
					);
				})
			);
		}
		return _observableOf<any>(<any>null);
	}

	downloadQuoteReport(
		quoteId: number,
		addToDocuments: boolean
	): Observable<any> {
		let url_ = environment.API_BASE_URL + '/api/reports/QuoteReport?';

		if (quoteId === undefined || quoteId === null) {
			throw new Error(
				"The parameter 'quoteId' must be defined and cannot be null."
			);
		} else {
			url_ += 'quoteId=' + encodeURIComponent('' + quoteId) + '&';
		}
		if (addToDocuments === undefined || addToDocuments === null) {
			throw new Error(
				"The parameter 'addToDocuments' must be defined and cannot be null."
			);
		} else {
			url_ +=
				'addToDocuments=' +
				encodeURIComponent('' + addToDocuments) +
				'&';
		}
		url_ = url_.replace(/[?&]$/, '');

		const options_: any = {
			observe: 'response',
			responseType: 'blob',
			headers: new HttpHeaders({
				'Content-Type': 'application/msword',
				Accept: 'application/msword',
			}),
		};

		return this.http
			.request('get', url_, options_)
			.pipe(
				_observableMergeMap((response_: any) => {
					return this.processDownloadQuoteReport(response_);
				}),
				map((response) => {
					const a = document.createElement('a');
					a.href = window.URL.createObjectURL(response.body);
					a.download = getFilenameFromHttpResponse(response);
					a.click();
				})
			)
			.pipe(
				_observableCatch((response_: any) => {
					if (response_ instanceof HttpResponseBase) {
						try {
							const t = this.processDownloadQuoteReport(
								<any>response_
							);

							return t;
						} catch (e) {
							return <Observable<any>>(<any>_observableThrow(e));
						}
					} else {
						return <Observable<any>>(
							(<any>_observableThrow(response_))
						);
					}
				})
			);
	}

	protected processDownloadQuoteReport(
		response: HttpResponseBase
	): Observable<any> {
		const status = response.status;
		const responseBlob =
			response instanceof HttpResponse
				? response
				: (<any>response).error instanceof Blob
				? (<any>response).error
				: undefined;

		const _headers: any = {};
		if (response.headers) {
			for (const key of response.headers.keys()) {
				_headers[key] = response.headers.get(key);
			}
		}
		if (status === 200) {
			return _observableOf(responseBlob);
		} else if (status !== 200 && status !== 204) {
			return blobToText(responseBlob).pipe(
				_observableMergeMap((_responseText) => {
					return throwException(
						'An unexpected server error occurred.',
						status,
						_responseText,
						_headers
					);
				})
			);
		}
		return _observableOf<any>(<any>null);
	}

	prevailingWagePayrollReport(jobId: number): Observable<any> {
		if (jobId === undefined || jobId === null) {
			throw new Error("The parameter 'jobId' must be defined.");
		}
		let url_ =
			environment.API_BASE_URL +
			'/api/reports/PrevailingWagePayrollReport/{jobId}';
		url_ = url_.replace('{jobId}', encodeURIComponent('' + jobId));
		url_ = url_.replace(/[?&]$/, '');

		const options_: any = {
			observe: 'response',
			responseType: 'blob',
			headers: new HttpHeaders({
				'Content-Type': 'application/json',
				Accept: 'application/pdf',
			}),
		};

		return this.http
			.request('get', url_, options_)
			.pipe(
				_observableMergeMap((response_: any) => {
					return this.processPrevailingWagePayrollReport(response_);
				}),
				map((response: any) => {
					if (response) {
						const a = document.createElement('a');
						a.href = window.URL.createObjectURL(response);
						a.download = `${moment
							.utc()
							.local()
							.format(
								'YYYY-MM-DD_HH-mm'
							)}_Prevailing_Wage_Payroll_Report.pdf`;
						a.click();

						return true;
					} else {
						return false;
					}
				})
			)
			.pipe(
				_observableCatch((response_: any) => {
					if (response_ instanceof HttpResponseBase) {
						try {
							return this.processPrevailingWagePayrollReport(
								<any>response_
							);
						} catch (e) {
							return <Observable<any>>(<any>_observableThrow(e));
						}
					} else {
						return <Observable<any>>(
							(<any>_observableThrow(response_))
						);
					}
				})
			);
	}

	protected processPrevailingWagePayrollReport(
		response: HttpResponseBase
	): Observable<any> {
		const status = response.status;
		const responseBlob =
			response instanceof HttpResponse
				? response.body
				: (<any>response).error instanceof Blob
				? (<any>response).error
				: undefined;

		const _headers: any = {};
		if (response.headers) {
			for (const key of response.headers.keys()) {
				_headers[key] = response.headers.get(key);
			}
		}
		if (status === 200) {
			return _observableOf(responseBlob);
		} else if (status !== 200 && status !== 204) {
			return blobToText(responseBlob).pipe(
				_observableMergeMap((_responseText) => {
					return throwException(
						'An unexpected server error occurred.',
						status,
						_responseText,
						_headers
					);
				})
			);
		}
		return _observableOf<any>(<any>null);
	}

	excludedPaycheckCsv(payrollBatchId: number): Observable<any> {
		let url_ =
			environment.API_BASE_URL +
			'/api/reports/excludedPaycheckCsv/{payrollBatchId}';
		if (payrollBatchId === undefined || payrollBatchId === null) {
			throw new Error("The parameter 'payrollBatchId' must be defined.");
		}
		url_ = url_.replace(
			'{payrollBatchId}',
			encodeURIComponent('' + payrollBatchId)
		);
		url_ = url_.replace(/[?&]$/, '');

		const options_: any = {
			observe: 'response',
			responseType: 'blob',
			headers: new HttpHeaders({
				Accept: 'application/json',
			}),
		};

		return this.http
			.request('get', url_, options_)
			.pipe(
				_observableMergeMap((response_: any) => {
					return this.processExcludedPaycheckCsv(response_);
				}),
				map((response: any) => {
					if (response) {
						const a = document.createElement('a');
						a.href = window.URL.createObjectURL(response);
						a.download = `${moment
							.utc()
							.local()
							.format(
								'YYYY-MM-DD_HH-mm'
							)}_Excluded_Paycheck_Data.csv`;
						a.click();

						return true;
					} else {
						return false;
					}
				})
			)
			.pipe(
				_observableCatch((response_: any) => {
					if (response_ instanceof HttpResponseBase) {
						try {
							return this.processExcludedPaycheckCsv(
								<any>response_
							);
						} catch (e) {
							return <Observable<any>>(<any>_observableThrow(e));
						}
					} else {
						return <Observable<any>>(
							(<any>_observableThrow(response_))
						);
					}
				})
			);
	}

	protected processExcludedPaycheckCsv(
		response: HttpResponseBase
	): Observable<any> {
		const status = response.status;
		const responseBlob =
			response instanceof HttpResponse
				? response.body
				: (<any>response).error instanceof Blob
				? (<any>response).error
				: undefined;

		const _headers: any = {};
		if (response.headers) {
			for (const key of response.headers.keys()) {
				_headers[key] = response.headers.get(key);
			}
		}
		if (status === 200) {
			return _observableOf(responseBlob);
		} else if (status !== 200 && status !== 204) {
			return blobToText(responseBlob).pipe(
				_observableMergeMap((_responseText) => {
					return throwException(
						'An unexpected server error occurred.',
						status,
						_responseText,
						_headers
					);
				})
			);
		}
		return _observableOf<any>(<any>null);
	}
}

function blobToText(blob: any): Observable<string> {
	return new Observable<string>((observer: any) => {
		if (!blob) {
			observer.next('');
			observer.complete();
		} else {
			const reader = new FileReader();
			reader.onload = function () {
				observer.next(this.result);
				observer.complete();
			};
			reader.readAsText(blob);
		}
	});
}

export class ReportsException extends Error {
	message: string;
	status: number;
	response: string;
	headers: { [key: string]: any };
	result: any;

	constructor(
		message: string,
		status: number,
		response: string,
		headers: { [key: string]: any },
		result: any
	) {
		super();

		this.message = message;
		this.status = status;
		this.response = response;
		this.headers = headers;
		this.result = result;
	}

	protected isReportsException = true;

	static isReportsException(obj: any): obj is ReportsException {
		return obj.isReportsException === true;
	}
}

function throwException(
	message: string,
	status: number,
	response: string,
	headers: { [key: string]: any },
	result?: any
): Observable<any> {
	if (result !== null && result !== undefined) {
		return _observableThrow(result);
	} else {
		return _observableThrow(
			new ReportsException(message, status, response, headers, null)
		);
	}
}
